import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatDate } from 'lib/functions/utils';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { getMajorBook } from '../../../../../../lib/api/accounting';
import GlobalSearcher, {
  Filter,
} from '../../../../../../lib/common/GlobalSearcher';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import { fetchWithDateChunking } from '../utils/fetchWithDateChunking';
import {
  deepMergeGroupedData,
  mergeMajorBookAccounts,
} from '../utils/mergeStrategies';
import MajorBookEntriesTable from './components';
import { exportMajorBookToExcel } from './utils/exportToExcel';

dayjs.extend(utc);

interface responseType {
  majorBookAccounts: {
    groupedLines: {
      date: string;
      businessIdentifier: string;
      businessName: string;
    }[];
  }[];
  groupedByCategoryAndBusiness?: any;
}

const MajorBook: React.FC<{
  transactional?: boolean;
  startDate?: Date;
  endDate?: Date;
}> = ({
  transactional = false,
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  const [data, setData] = useState<responseType>({
    majorBookAccounts: [],
    groupedByCategoryAndBusiness: {},
  });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  // Initialize with current month
  const now = dayjs();
  const startDateDefault = now.startOf('month').toDate();
  const endDateDefault = now.endOf('month').toDate();
  const [accountCode, setAccountCode] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(
    propStartDate
      ? propStartDate.toISOString().split('T')[0]
      : startDateDefault.toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState<string | null>(
    propEndDate
      ? propEndDate.toISOString().split('T')[0]
      : endDateDefault.toISOString().split('T')[0],
  );

  const triggerSearch = async () => {
    await setUp(startDate || undefined, endDate || undefined);
  };

  const setUp = async (
    startDateParam?: string,
    endDateParam?: string,
    showLoading: boolean = true,
  ) => {
    try {
      if (showLoading) setLoading(true);
      if (accountCode) {
        let queryParams = `accountCode=${accountCode}`;
        if (startDateParam) queryParams += `&startDate=${startDateParam}`;
        if (endDateParam) queryParams += `&endDate=${endDateParam}`;
        const result = await getMajorBook(dispatch, queryParams);
        setData(result);
        if (showLoading) setLoading(false);
        return;
      }
      if (startDateParam && endDateParam) {
        const result = await fetchWithDateChunking({
          startDate: startDateParam || '',
          endDate: endDateParam || '',
          thresholdMonths: 6,
          apiFunction: getMajorBook,
          mergeStrategy: (responses) => {
            const allMajorBookAccounts = responses.map(
              (response) => response?.majorBookAccounts || [],
            );
            const allGroupedResponses = responses.map(
              (response) => response?.groupedByCategoryAndBusiness || {},
            );

            const mergedMajorBookAccounts =
              mergeMajorBookAccounts(allMajorBookAccounts);
            const sortedMergedMajorBookAccounts = mergedMajorBookAccounts.sort(
              (a, b) => a.account.localeCompare(b.account),
            );

            return {
              majorBookAccounts: sortedMergedMajorBookAccounts,
              groupedByCategoryAndBusiness:
                deepMergeGroupedData(allGroupedResponses),
            };
          },
          additionalParams: { transactional, accountCode: accountCode || '' },
          dispatch,
        });
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching major book:', error);
    }
    if (showLoading) setLoading(false);
  };

  const handleExport = async () => {
    if ((!startDate || !endDate) && !accountCode)
      return showAlert(
        dispatch,
        'error',
        'Fecha de inicio y fin son requeridas o el código de cuenta contable es requerido',
      );
    void exportMajorBookToExcel(data, {
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
      businessIdentifier: user?.businessIdentifier,
      businessName: user?.companyName,
      accountCode: accountCode || undefined,
    });
  };

  useEffect(() => {
    if (propStartDate && propEndDate) {
      // Use props if available
      const startDateString = propStartDate.toISOString().split('T')[0];
      const endDateString = propEndDate.toISOString().split('T')[0];
      void setUp(startDateString, endDateString);
    } else {
      // Use default dates for initial load
      void setUp(
        formatDate(startDateDefault, 'dd-mm-yyyy'),
        formatDate(endDateDefault, 'dd-mm-yyyy'),
      );
    }
  }, [propStartDate, propEndDate]);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <div>
        <HeaderWithActions
          title={`Período: ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}`}
          variant="body1"
          item={
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}
            >
              <div>
                <GlobalSearcher
                  filters={
                    [
                      {
                        type: 'date',
                        label: 'Fecha de inicio',
                        dateKey: 'startDate',
                        value: startDate,
                        setter: setStartDate,
                      },
                      {
                        type: 'date',
                        label: 'Fecha de fin',
                        dateKey: 'endDate',
                        value: endDate,
                        setter: setEndDate,
                      },
                      ...(!transactional
                        ? [
                            {
                              type: 'text',
                              label: 'Código cuenta contable',
                              value: accountCode,
                              setter: setAccountCode,
                            },
                          ]
                        : []),
                    ] as Filter[]
                  }
                  triggerSearch={triggerSearch}
                  shortcuts={true}
                />
              </div>
              <div>
                <CustomButton color="primary" onClick={handleExport}>
                  Exportar
                </CustomButton>
              </div>
            </div>
          }
        />
      </div>
      <div style={{ marginTop: 20 }}>
        <MajorBookEntriesTable
          entries={data}
          refreshData={async (showLoading = true) => {
            await setUp(
              startDate || undefined,
              endDate || undefined,
              showLoading,
            );
          }}
          startDate={startDate ? new Date(startDate) : undefined}
          endDate={endDate ? new Date(endDate) : undefined}
        />
      </div>
    </div>
  );
};

export default MajorBook;
